import React from 'react';

import { setSnackbar } from 'flow/actions/app-actions';
import { rotateMeetingData } from 'flow/actions/meeting-actions';
import DropdownMenuComponent from 'flow/components/ui-components/DropdownMenuComponent';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import NumberWrapper from 'flow/components/ui-components/NumberWrapper';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useMeetingCountryNumber from 'flow/hooks/useMeetingCountryNumber';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { selectLobbyViewerCanRegenerateLink } from 'flow/selectors/meeting-selectors';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { formatPin } from 'flow/utils/meeting-utils';

type Props = {
    meetingCode: string;
    pin?: string;
    number: string;
    shouldShowVideo?: boolean;
};

const rowButtonClassName =
    '-mx-2 flex cursor-pointer items-center gap-2 rounded-lg border-none bg-transparent px-2 py-1 text-start text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent';

// Pixel-matched to the "Joining info" host modal (Meetings UX bug board
// Figma, VID-308): a compact info block plus two icon-row actions, replacing
// MeetingJoinInfo's heavier QR-code/email layout for this surface only.
const MeetingLobbyJoinInfoContent = ({
    meetingCode,
    pin,
    number,
    shouldShowVideo = true,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const canRegenerateLink = useAppSelector(
        selectLobbyViewerCanRegenerateLink,
    );

    const {
        countryCode,
        setCountryCode,
        countryOptions,
        countryNumber,
        conferenceLink,
    } = useMeetingCountryNumber(meetingCode, number);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            dispatch(
                setSnackbar({
                    message: i18next.t('snackbar.copied_to_clipboard'),
                }),
            );
        });
    };

    const copyJoiningDetails = () =>
        copyToClipboard(
            [
                shouldShowVideo && conferenceLink,
                `${i18next.t('videoconference.controlpanel.dial_in')} ${countryNumber}`,
                pin &&
                    `${i18next.t('videoconference.controlpanel.pin_short')} ${pin}`,
            ]
                .filter(Boolean)
                .join('\n'),
        );

    const handleRegenerate = () =>
        openModal('confirmationDialog', {
            title: i18next.t(
                'videoconference.join_info.rotate_meeting_data.header',
            ),
            description: (
                <>
                    {i18next.t(
                        'videoconference.join_info.rotate_meeting_data.description',
                    )}
                    <br />
                    <span className="font-bold">
                        {i18next.t(
                            'videoconference.join_info.rotate_meeting_data.warning',
                        )}
                    </span>
                </>
            ),
            confirmButtonText: i18next.t(
                'videoconference.join_info.rotate_meeting_data.confirm',
            ),
            confirmButtonColor: 'danger',
            handleAccept: () => dispatch(rotateMeetingData(meetingCode)),
        });

    const regenerateButton = (
        <button
            className={rowButtonClassName}
            onClick={handleRegenerate}
            type="button"
        >
            <Icon iconKey="reload" size="1rem" />
            {i18next.t('videoconference.join_info.rotate_meeting_data.button')}
        </button>
    );

    const regenerateButtonRow: React.ReactNode =
        ownExtensionKey && shouldShowVideo && canRegenerateLink
            ? regenerateButton
            : null;

    return (
        <div className="flex flex-col gap-3 px-2 text-sm">
            <DropdownMenuComponent
                options={countryOptions}
                title={
                    countryOptions.find((option) => option.key === countryCode)
                        ?.label
                }
                onItemSelected={setCountryCode}
                disabled={IS_UAE_HOST}
                menuButtonClassName="!w-auto !gap-1 !bg-transparent !text-gray-800 !p-0 !text-sm !font-normal"
            />

            {shouldShowVideo && (
                <div className="flex items-center gap-2">
                    <div className="truncate text-gray-600">
                        {conferenceLink}
                    </div>
                    <IconButton
                        onClick={() => copyToClipboard(conferenceLink)}
                        className="ms-auto size-6 shrink-0"
                        tooltipLabel={i18next.t(
                            'videoconference.join_info.copy_link',
                        )}
                    >
                        <Icon
                            iconKey="copyOutlined"
                            size="1rem"
                            className="text-gray-600"
                        />
                    </IconButton>
                </div>
            )}

            <div className="text-gray-800">
                {i18next.t('videoconference.controlpanel.dial_in')}{' '}
                <NumberWrapper>{countryNumber}</NumberWrapper>
            </div>

            {pin && (
                <div className="text-gray-800">
                    {i18next.t('videoconference.controlpanel.pin_short')}{' '}
                    <NumberWrapper>{formatPin(pin)}</NumberWrapper>
                </div>
            )}

            <button
                className={rowButtonClassName}
                onClick={copyJoiningDetails}
                type="button"
            >
                <Icon iconKey="copyOutlined" size="1rem" />
                {i18next.t('videoconference.controlpanel.copy_joining_details')}
            </button>

            {regenerateButtonRow}
        </div>
    );
};

export default MeetingLobbyJoinInfoContent;
